// Orchestration → Signals page.
// All: fleet table of every non-terminal trigger signal.
// Create: pick group + target (thread / all-live) → same wizard.
import { useClient } from '@cortex/react-query';
import type {
  IPaginated,
  PersistentAgentFleetWatchRow,
  PersistentAgentGroup,
  PersistentAgentListItem,
  PersistentAgentWatch,
  StampPersistentAgentWatchInput,
  StampPersistentAgentWatchResult,
} from '@cortex/types';
import { parseCompiledWatchQuery } from '@cortex/types';
import { fork } from '@cortex/utils/fork';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { CompactSelect } from '../../../components/common/CompactSelect';
import { PageHeader } from '../../../components/common/PageHeader';
import { useDebouncedValue } from '../../../components/inbox/use-debounced-value';
import { WatchesPanel } from '../../../components/persistent-agents/WatchesPanel';
import { readApiErrorBody } from '../../../lib/api-errors';
import { routes } from '../../../lib/constants';
import {
  fleetWatchPageSize,
  fleetWatchesSearchParams,
  listPageMax,
  pageWindowLabel,
  argsSummary,
  stampConfirmMessage,
  stampForceMessage,
  stampResultMessage,
  truncationNotice,
} from '../../../lib/fleet-watches';
import { formatRelative } from '../../../lib/format';

export interface IWatchesSearch {
  /** all = fleet table (default); create = wizard launchpad */
  tab?: 'all' | 'create';
  /** Create target helpers */
  groupId?: string;
  threadId?: string;
  agentId?: string;
  /** create scope */
  scope?: 'thread' | 'all-live';
}

export const Route = createFileRoute('/agents/orchestration/signals')({
  validateSearch: (raw: Record<string, unknown>): IWatchesSearch => {
    // Back-compat: old mode=group → create + group scope
    return {
      tab: raw.tab === 'create' ? 'create' : 'all',
      groupId: typeof raw.groupId === 'string' && raw.groupId ? raw.groupId : undefined,
      threadId: typeof raw.threadId === 'string' && raw.threadId ? raw.threadId : undefined,
      agentId: typeof raw.agentId === 'string' && raw.agentId ? raw.agentId : undefined,
      scope: raw.scope === 'all-live' || raw.scope === 'thread' ? raw.scope : undefined,
    };
  },
  component: OrchestrationWatchesPage,
});

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

const statusBadge: Record<string, string> = {
  STARTING: 'badge badge--warning',
  IDLE: 'badge badge--neutral',
  WORKING: 'badge badge--success',
  PAUSED: 'badge badge--warning',
  WAITING_FOR_INPUT: 'badge badge--warning',
  STOPPED: 'badge badge--default',
  FAILED: 'badge badge--danger',
};

function watchModeLabel(w: PersistentAgentWatch): string {
  if (w.code && parseCompiledWatchQuery(w.code)) {
    return w.condition ? 'list · filter' : 'list · thin';
  }
  if (w.code) return 'map JS';
  if (w.condition) return 'value · gate';
  if (w.selector) return 'value';
  return 'whole payload';
}

function OrchestrationWatchesPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const tab = search.tab ?? 'all';

  return (
    <div className="page">
      <PageHeader title="Signals" />
      <p className="t-sm t-muted" style={{ margin: '-8px 0 16px' }}>
        Fleet sensors: MCP pollers that write fact lanes and wake threads. Prefer thread-scoped
        watches for missions and trials.
      </p>

      <div
        role="tablist"
        aria-label="Watches views"
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 'var(--sp-3)',
          borderBottom: '1px solid var(--border-subtle)',
        }}
      >
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'all'}
          className={tab === 'all' ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
          onClick={() =>
            fork(navigate({ to: routes.ORCHESTRATION_SIGNALS, search: { tab: 'all' } }))
          }
        >
          All watches
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'create'}
          className={tab === 'create' ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
          onClick={() =>
            fork(
              navigate({
                to: routes.ORCHESTRATION_SIGNALS,
                search: {
                  tab: 'create',
                  scope: search.scope ?? 'thread',
                  ...(search.groupId ? { groupId: search.groupId } : {}),
                  ...(search.threadId ? { threadId: search.threadId } : {}),
                  ...(search.agentId ? { agentId: search.agentId } : {}),
                },
              }),
            )
          }
        >
          Create
        </button>
      </div>

      {tab === 'create' ? <CreateWatchFlow /> : <AllWatchesFleet />}
    </div>
  );
}

// ── All watches (fleet) ─────────────────────────────────────────────────────

function AllWatchesFleet() {
  const client = useClient();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const debouncedQ = useDebouncedValue(q.trim(), 300);
  const [statusFilter, setStatusFilter] = useState<'ACTIVE' | 'PAUSED' | 'all'>('ACTIVE');
  const [groupFilter, setGroupFilter] = useState<string>('all');
  const [showTerminalThreads, setShowTerminalThreads] = useState(false);
  const [offset, setOffset] = useState(0);

  // Any filter change starts again from the first page (reset in the same
  // event, so no request goes out for the new filter at the old offset).
  const onFilterChange =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v);
      setOffset(0);
    };

  const groupsQuery = useGroupsList();
  const groups = useMemo(() => groupsQuery.data?.items ?? [], [groupsQuery.data]);
  const groupsNotice = truncationNotice(groups.length, groupsQuery.data?.total ?? 0, 'group');

  // One paged, server-filtered request for every fleet thread's watches,
  // instead of one request per group plus one per live thread. Only the page
  // on screen is polled. With refetchIntervalInBackground false, react-query
  // skips interval refetches while the tab is hidden and refetches on focus.
  const filters = {
    status: statusFilter,
    groupId: groupFilter,
    q: debouncedQ,
    includeTerminalThreads: showTerminalThreads,
    offset,
  };
  const watchesQuery = useQuery({
    queryKey: ['persistent-agent-watches', 'fleet', filters],
    queryFn: () =>
      client.http
        .get('v1/persistent-agent-groups/watches', {
          searchParams: fleetWatchesSearchParams(filters),
        })
        .json<IPaginated<PersistentAgentFleetWatchRow>>(),
    placeholderData: keepPreviousData,
    refetchInterval: 8000,
    refetchIntervalInBackground: false,
  });
  const rows = watchesQuery.data?.items ?? [];
  const total = watchesQuery.data?.total ?? 0;
  const loading = groupsQuery.isLoading || watchesQuery.isLoading;

  const patchWatch = useMutation({
    mutationFn: async (args: {
      groupId: string;
      threadId: string;
      name: string;
      status: 'ACTIVE' | 'PAUSED';
    }) => {
      return client.http
        .patch(
          `v1/persistent-agent-groups/${args.groupId}/agents/${args.threadId}/watches/${encodeURIComponent(args.name)}`,
          { json: { status: args.status } },
        )
        .json();
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['persistent-agent-watches'] });
    },
  });

  const deleteWatch = useMutation({
    mutationFn: async (args: { groupId: string; threadId: string; name: string }) => {
      return client.http
        .delete(
          `v1/persistent-agent-groups/${args.groupId}/agents/${args.threadId}/watches/${encodeURIComponent(args.name)}`,
        )
        .text();
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['persistent-agent-watches'] });
    },
  });

  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 12,
          alignItems: 'center',
        }}
      >
        <input
          className="input"
          style={{ minWidth: 200, flex: '1 1 200px' }}
          placeholder="Search name, tool, lane, thread…"
          value={q}
          onChange={(e) => onFilterChange(setQ)(e.target.value)}
        />
        <select
          className="select-native"
          style={{ width: 140 }}
          value={statusFilter}
          onChange={(e) => onFilterChange(setStatusFilter)(e.target.value as typeof statusFilter)}
        >
          <option value="ACTIVE">Active</option>
          <option value="PAUSED">Paused</option>
          <option value="all">All statuses</option>
        </select>
        <CompactSelect
          ariaLabel="Group"
          width={200}
          value={groupFilter === 'all' ? '' : groupFilter}
          onChange={(next) => onFilterChange(setGroupFilter)(next || 'all')}
          emptyOptionLabel="All groups"
          placeholder="All groups"
          options={groups.map((g) => ({ value: g.id, label: g.name }))}
        />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
          <input
            type="checkbox"
            checked={showTerminalThreads}
            onChange={(e) => onFilterChange(setShowTerminalThreads)(e.target.checked)}
          />
          Include stopped threads
        </label>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          style={{ marginLeft: 'auto' }}
          onClick={() =>
            fork(navigate({ to: routes.ORCHESTRATION_SIGNALS, search: { tab: 'create' } }))
          }
        >
          Create watch
        </button>
      </div>

      {groupsNotice && (
        <p className="t-xs t-muted" style={{ margin: '0 0 8px' }}>
          {groupsNotice}
        </p>
      )}

      {loading && rows.length === 0 ? (
        <span className="t-muted t-sm">Loading watches…</span>
      ) : watchesQuery.isError ? (
        <div className="card" style={{ padding: 16 }}>
          <p className="t-sm" style={{ margin: 0 }}>
            Could not load watches: {(watchesQuery.error as Error).message}
          </p>
        </div>
      ) : rows.length === 0 ? (
        <div className="card" style={{ padding: 16 }}>
          <p className="t-sm t-muted" style={{ margin: 0 }}>
            No live watches match. Create one to push world changes into fact lanes and wake
            threads.
          </p>
          {offset > 0 && (
            // The pager only renders in the non-empty branch, so a page that
            // emptied out (a row deleted, or paused out of the filter by the
            // 8 s poll) would strand the table here with no way back.
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              style={{ marginTop: 10, marginRight: 8 }}
              disabled={watchesQuery.isFetching}
              onClick={() =>
                setOffset(
                  Math.max(0, (Math.ceil(total / fleetWatchPageSize) - 1) * fleetWatchPageSize),
                )
              }
            >
              Back to the last page with watches
            </button>
          )}
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            style={{ marginTop: 10 }}
            onClick={() =>
              fork(navigate({ to: routes.ORCHESTRATION_SIGNALS, search: { tab: 'create' } }))
            }
          >
            Create watch
          </button>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'auto' }}>
          <table className="table" style={{ width: '100%', fontSize: 13 }}>
            <thead>
              <tr>
                <th>Status</th>
                <th>Name</th>
                <th>Group</th>
                <th>Thread</th>
                <th>Tool</th>
                <th>Mode</th>
                <th>Lane</th>
                <th>Delivery</th>
                <th>Last poll</th>
                <th>Err</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.watch.id}>
                  <td>
                    <span
                      className={
                        r.watch.status === 'ACTIVE'
                          ? 'badge badge--success'
                          : 'badge badge--warning'
                      }
                    >
                      {r.watch.status}
                    </span>
                  </td>
                  <td>
                    <code>{r.watch.name}</code>
                  </td>
                  <td>{r.groupName}</td>
                  <td>
                    {r.threadId ? (
                      <Link to={routes.orchestrationThread(r.groupId, r.threadId)} className="t-sm">
                        {r.threadName ?? r.threadId}
                        {r.threadStatus ? (
                          <span
                            className={statusBadge[r.threadStatus] ?? 'badge'}
                            style={{ marginLeft: 6 }}
                          >
                            {r.threadStatus}
                          </span>
                        ) : null}
                      </Link>
                    ) : (
                      <span className="t-muted">—</span>
                    )}
                  </td>
                  <td>
                    <span className="t-mono t-xs">{r.watch.toolName}</span>
                  </td>
                  <td className="t-xs">{watchModeLabel(r.watch)}</td>
                  <td>
                    <code className="t-xs">{r.watch.laneId ?? '—'}</code>
                  </td>
                  <td className="t-xs">{r.watch.wakeDelivery}</td>
                  <td className="t-xs t-muted">
                    {r.watch.lastPolledAt ? formatRelative(r.watch.lastPolledAt) : 'never'}
                  </td>
                  <td>
                    {r.watch.consecutiveErrors > 0 ? (
                      <span className="badge badge--danger">{r.watch.consecutiveErrors}</span>
                    ) : (
                      '0'
                    )}
                  </td>
                  <td>
                    <span style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        disabled={patchWatch.isPending}
                        onClick={() =>
                          patchWatch.mutate({
                            groupId: r.groupId,
                            threadId: r.threadId,
                            name: r.watch.name,
                            status: r.watch.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE',
                          })
                        }
                      >
                        {r.watch.status === 'ACTIVE' ? 'Pause' : 'Resume'}
                      </button>
                      {r.threadId ? (
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() =>
                            fork(
                              navigate({
                                to: routes.ORCHESTRATION_SIGNALS,
                                search: {
                                  tab: 'create',
                                  scope: 'thread',
                                  groupId: r.groupId,
                                  agentId: r.groupId,
                                  threadId: r.threadId,
                                },
                              }),
                            )
                          }
                        >
                          Edit
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="btn btn--destructive btn--sm"
                        disabled={deleteWatch.isPending}
                        onClick={() => {
                          if (!confirm(`Delete watch "${r.watch.name}"?`)) return;
                          deleteWatch.mutate({
                            groupId: r.groupId,
                            threadId: r.threadId,
                            name: r.watch.name,
                          });
                        }}
                      >
                        Delete
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div
            className="t-xs t-muted"
            style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <span>
              {pageWindowLabel(offset, rows.length, total, 'watch', 'watches')}
              {statusFilter === 'ACTIVE' ? ' · active only' : ''}
            </span>
            {total > fleetWatchPageSize && (
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={offset === 0 || watchesQuery.isFetching}
                  onClick={() => setOffset(Math.max(0, offset - fleetWatchPageSize))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={!watchesQuery.data?.hasMore || watchesQuery.isFetching}
                  onClick={() => setOffset(offset + fleetWatchPageSize)}
                >
                  Next
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Shared list queries ─────────────────────────────────────────────────────

/** Groups, with an explicit page size so a cut list is visible, not silent. */
function useGroupsList() {
  const client = useClient();
  return useQuery({
    queryKey: ['persistent-agents', { limit: listPageMax }],
    queryFn: () =>
      client.http
        .get('v1/persistent-agent-groups', { searchParams: { limit: listPageMax } })
        .json<IListEnvelope<PersistentAgentGroup>>(),
  });
}

/**
 * One group's live threads for the pickers. Asks for the API maximum
 * explicitly; `total` drives the "showing N of total" notice.
 */
function useLiveGroupThreads(groupId: string) {
  const client = useClient();
  const query = useQuery({
    enabled: !!groupId,
    queryKey: ['persistent-agents', groupId, 'agents', { limit: listPageMax }],
    queryFn: () =>
      client.http
        .get(`v1/persistent-agent-groups/${groupId}/agents`, {
          searchParams: { limit: listPageMax },
        })
        .json<IListEnvelope<PersistentAgentListItem>>(),
    refetchInterval: 15000,
    refetchIntervalInBackground: false,
  });
  const threads = useMemo(() => query.data?.items ?? [], [query.data]);
  const liveThreads = useMemo(
    () => threads.filter((t) => t.status !== 'STOPPED' && t.status !== 'FAILED'),
    [threads],
  );
  return {
    threads,
    liveThreads,
    notice: truncationNotice(threads.length, query.data?.total ?? 0, 'thread'),
  };
}

function threadOption(t: PersistentAgentListItem) {
  return { value: t.id, label: (t.name ?? t.id).slice(0, 64), description: t.status };
}

// ── Create ──────────────────────────────────────────────────────────────────

function CreateWatchFlow() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const scope = search.scope ?? 'thread';

  const groupsQuery = useGroupsList();
  const groups = groupsQuery.data?.items ?? [];
  const groupsNotice = truncationNotice(groups.length, groupsQuery.data?.total ?? 0, 'group');

  const groupId = search.groupId ?? search.agentId ?? '';
  const threadId = search.threadId ?? '';
  const groupName = groups.find((g) => g.id === groupId)?.name ?? groupId;

  const { threads, liveThreads, notice: threadsNotice } = useLiveGroupThreads(groupId);
  const selectedThread = threads.find((t) => t.id === threadId) ?? null;

  const setSearch = (patch: Partial<IWatchesSearch>) => {
    fork(
      navigate({
        to: routes.ORCHESTRATION_SIGNALS,
        search: {
          tab: 'create',
          scope: patch.scope ?? scope,
          groupId: patch.groupId === undefined ? groupId || undefined : patch.groupId || undefined,
          agentId: patch.groupId === undefined ? groupId || undefined : patch.groupId || undefined,
          threadId:
            patch.threadId === undefined ? threadId || undefined : patch.threadId || undefined,
        },
      }),
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 920 }}>
      <div className="card" style={{ padding: 14 }}>
        <div className="field-label" style={{ marginBottom: 8 }}>
          1. Where does it live?
        </div>
        <div className="pa-watch-mode-grid">
          {(
            [
              ['thread', 'One thread', 'Canonical — sensor on a single live session'],
              [
                'all-live',
                'All live threads in group',
                'Stamp the same watch on every non-stopped session (bench kit)',
              ],
            ] as const
          ).map(([id, label, hint]) => (
            <button
              key={id}
              type="button"
              className={scope === id ? 'pa-watch-mode pa-watch-mode--on' : 'pa-watch-mode'}
              onClick={() =>
                setSearch({
                  scope: id,
                  threadId: id === 'thread' ? threadId : undefined,
                })
              }
            >
              <span className="pa-watch-mode__label">{label}</span>
              <span className="pa-watch-mode__hint">{hint}</span>
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 14 }}>
          <label className="field" style={{ flex: '1 1 220px' }}>
            <span className="field-label">Orchestration group</span>
            <CompactSelect
              ariaLabel="Orchestration group"
              width="100%"
              value={groupId}
              onChange={(next) =>
                setSearch({ groupId: next, threadId: undefined, agentId: next || undefined })
              }
              placeholder="Choose group…"
              options={groups.map((g) => ({ value: g.id, label: g.name }))}
            />
          </label>

          {scope === 'thread' && (
            <label className="field" style={{ flex: '1 1 220px' }}>
              <span className="field-label">Thread</span>
              <CompactSelect
                ariaLabel="Thread"
                width="100%"
                value={threadId}
                disabled={!groupId}
                onChange={(next) => setSearch({ threadId: next })}
                placeholder={groupId ? 'Choose live thread…' : 'Pick a group first'}
                options={liveThreads.map(threadOption)}
              />
            </label>
          )}
        </div>
        {(groupsNotice || (groupId && threadsNotice)) && (
          <p className="t-xs t-muted" style={{ margin: '8px 0 0' }}>
            {[groupsNotice, groupId ? threadsNotice : null].filter(Boolean).join(' ')}
          </p>
        )}
      </div>

      {scope === 'thread' && groupId && threadId ? (
        <div>
          <div className="field-label" style={{ marginBottom: 8 }}>
            2. Configure the watch
          </div>
          <WatchesPanel
            agentId={groupId}
            threadId={threadId}
            status={selectedThread?.status ?? null}
            hideList={true}
            startInCreate={true}
            onSaved={() =>
              fork(navigate({ to: routes.ORCHESTRATION_SIGNALS, search: { tab: 'all' } }))
            }
          />
        </div>
      ) : scope === 'all-live' && groupId ? (
        <AllLiveCreateStamp groupId={groupId} groupName={groupName} liveThreads={liveThreads} />
      ) : (
        <p className="t-sm t-muted">
          Select a group{scope === 'thread' ? ' and thread' : ''} to continue.
        </p>
      )}
    </div>
  );
}

/**
 * Create on a template thread, then stamp one of its operator watches onto
 * every other live thread of the group in ONE server-side call. The operator
 * picks the exact watch; the server's dry run (over ALL live threads, not
 * the loaded picker page) supplies the count, tool and args, which are shown
 * and confirmed before the write. The write passes the dry run's target hash
 * back, so a group whose threads changed in between is refused, and a tool
 * that does not look read-only needs a second, explicit confirmation.
 */
function AllLiveCreateStamp({
  groupId,
  groupName,
  liveThreads,
}: {
  groupId: string;
  groupName: string;
  liveThreads: PersistentAgentListItem[];
}) {
  const client = useClient();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [templateId, setTemplateId] = useState(liveThreads[0]?.id ?? '');
  const [watchName, setWatchName] = useState('');
  const [stampMsg, setStampMsg] = useState<string | null>(null);
  const [stamping, setStamping] = useState(false);

  useEffect(() => {
    if (!templateId && liveThreads[0]) setTemplateId(liveThreads[0].id);
  }, [liveThreads, templateId]);

  const template = liveThreads.find((t) => t.id === templateId) ?? liveThreads[0];

  const stamp = (body: StampPersistentAgentWatchInput) =>
    client.http
      .post(`v1/persistent-agent-groups/${groupId}/watches/stamp`, { json: body })
      .json<StampPersistentAgentWatchResult>();

  // Server dry run: stampable watches on the template, the chosen watch's
  // summary, and the count of every other live thread.
  const previewQuery = useQuery({
    enabled: !!template,
    // Under the template's watch key, so saving a watch in the wizard below
    // (which invalidates that key) refreshes the list and count.
    queryKey: ['persistent-agent-watches', template?.id, 'stamp-preview', groupId, watchName],
    queryFn: () =>
      stamp({
        templateThreadId: template?.id ?? '',
        ...(watchName ? { watchName } : {}),
        dryRun: true,
      }),
    refetchInterval: 15000,
    refetchIntervalInBackground: false,
  });
  const preview = previewQuery.data;
  const stampable = preview?.stampableWatches ?? [];

  // Keep the selection valid: auto-pick the only watch, drop a vanished one.
  useEffect(() => {
    if (!preview) return;
    if (watchName && !preview.stampableWatches.includes(watchName)) setWatchName('');
    else if (!watchName && preview.stampableWatches.length === 1) {
      setWatchName(preview.stampableWatches[0] ?? '');
    }
  }, [preview, watchName]);

  const stampFromTemplate = async () => {
    if (!template || !watchName) return;
    setStamping(true);
    setStampMsg(null);
    try {
      // Fresh dry run right before asking, so the confirmation is current.
      // `fresh` skips the server's short-lived share-scope cache for this one.
      const dry = await stamp({
        templateThreadId: template.id,
        watchName,
        dryRun: true,
        fresh: true,
      });
      if (!dry.watch) {
        setStampMsg(`Watch "${watchName}" is no longer on the template thread.`);
        return;
      }
      if (dry.matched === 0) {
        setStampMsg('No other live threads in this group to stamp.');
        return;
      }
      if (!confirm(stampConfirmMessage(dry.watch, dry.matched, groupName))) return;
      const force = !dry.watch.readOnly;
      if (force && !confirm(stampForceMessage(dry.watch, dry.matched))) return;
      const res = await stamp({
        templateThreadId: template.id,
        watchName: dry.watch.name,
        expectedTargetsHash: dry.targetsHash,
        // Pins the watch itself as well, so an edit between this dry run and
        // the write is a 409 rather than a fleet-wide surprise. The server
        // requires it, so send it unconditionally: a missing hash is a 400
        // with the fix in it rather than a silent fleet-wide stamp.
        expectedWatchHash: dry.watchHash ?? '',
        ...(force ? { force: true } : {}),
      });
      setStampMsg(stampResultMessage(res));
      await qc.invalidateQueries({ queryKey: ['persistent-agent-watches'] });
      if (res.failed.length === 0) {
        fork(navigate({ to: routes.ORCHESTRATION_SIGNALS, search: { tab: 'all' } }));
      }
    } catch (err) {
      const body = await readApiErrorBody(err);
      setStampMsg(body?.message ?? (err instanceof Error ? err.message : String(err)));
    } finally {
      setStamping(false);
    }
  };

  if (liveThreads.length === 0) {
    return <p className="t-sm t-muted">No live threads in this group.</p>;
  }

  const countLabel = preview
    ? `${preview.matched.toLocaleString()} other live thread${preview.matched === 1 ? '' : 's'}`
    : 'every other live thread';

  return (
    <div>
      <div className="card" style={{ padding: 14, marginBottom: 12 }}>
        <div className="field-label" style={{ marginBottom: 6 }}>
          2. Build on a template thread, then stamp
        </div>
        <p className="t-xs t-muted" style={{ margin: '0 0 10px' }}>
          Configure the watch once below, then pick it and stamp it onto every other live thread.
          The copy runs on the server over all live threads in the group. Tool args are copied
          as-is; device and place anchors are not. You confirm the count, tool and args first.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <label className="field" style={{ flex: '1 1 260px', maxWidth: 360 }}>
            <span className="field-label">Template thread</span>
            <CompactSelect
              ariaLabel="Template thread"
              width="100%"
              value={template?.id ?? ''}
              onChange={(next) => {
                setTemplateId(next);
                setWatchName('');
              }}
              options={liveThreads.map(threadOption)}
            />
          </label>
          <label className="field" style={{ flex: '1 1 200px', maxWidth: 280 }}>
            <span className="field-label">Watch to stamp</span>
            <CompactSelect
              ariaLabel="Watch to stamp"
              width="100%"
              value={watchName}
              disabled={stampable.length === 0}
              onChange={(next) => setWatchName(next)}
              placeholder={
                stampable.length === 0
                  ? 'Save a watch below first (an existing watch must be re-saved once)'
                  : 'Choose watch…'
              }
              options={stampable.map((n) => ({ value: n, label: n }))}
            />
          </label>
        </div>
        {preview?.watch && (
          <p className="t-xs t-muted" style={{ margin: '8px 0 0' }}>
            <span className="t-mono">{preview.watch.toolName}</span> every{' '}
            {preview.watch.intervalSec}s · args{' '}
            <span className="t-mono">{argsSummary(preview.watch.args, 120)}</span>
            {preview.watch.readOnly ? '' : ' · not read-only, needs an extra confirmation'}
          </p>
        )}
        <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            disabled={stamping || !template || !watchName}
            onClick={() => {
              stampFromTemplate().catch(() => undefined);
            }}
          >
            {stamping
              ? 'Stamping…'
              : `Stamp ${watchName ? `"${watchName}"` : 'watch'} → ${countLabel}`}
          </button>
          {stampMsg && <span className="t-xs t-muted">{stampMsg}</span>}
        </div>
      </div>
      {template && (
        <WatchesPanel
          agentId={groupId}
          threadId={template.id}
          status={template.status}
          hideList={true}
          startInCreate={true}
        />
      )}
    </div>
  );
}

// ── Group mode (legacy fan-out) ─────────────────────────────────────────────
